<template>
  <div class="project-new">
    <rum-loader full v-show="loading" style="height: 79vh"/>
    <div v-show="!loading" class="project__form">
      <header class="project-form__body">
        <rum-label>Organization</rum-label>
        <rum-select
          placeholder="Select a project"
          v-model="$v.organization.$model"
          :options="administeredOrganizations"
          :clearable="false"
          @input="editProjectThrottled"
          label="name"
        />
        <div class="validation--error" v-if="$v.organization.$dirty && !$v.organization.required">Organization is required</div>

        <div class="project-form__row">
          <rum-label>Project Name</rum-label>
          <rum-input v-model="$v.project.name.$model" type="text" placeholder="Project Name" @blur="editProjectThrottled"/>
          <div class="validation--error" v-if="$v.project.name.$dirty && !$v.project.name.required">Name is required</div>
        </div>

        <div class="project-form__row">
          <rum-label>
            Internet Connectivity
            <i class="tooltip-icon fas fa-question-circle" v-tooltip="'Will Learners have broader internet access. This will control wether we show external links and media references.'"></i>
          </rum-label>
          <rum-toggle-button v-model="project.internet_connectivity" disabled :options="connectivityTypes" @input="editProject"></rum-toggle-button>
        </div>

        <div class="project-form__row">
          <rum-label>Project Language
            <i class="tooltip-icon fas fa-question-circle" v-tooltip="'The language used in the Learn app, and the language of the Bytes.'"></i>
          </rum-label>
          <rum-select placeholder="Select a language" v-model="$v.project.language.$model" :options="languagesIso" label="label" :clearable="false" @input="editProjectThrottled"></rum-select>
          <div class="validation--error" v-if="$v.project.language.$dirty && !$v.project.language.required">Language is required</div>
        </div>

        <div class="project-form__row">
          <rum-label>Project TimeZone</rum-label>
          <rum-timezone ref="timezone" placeholder="Select a timezone" v-model="$v.project.timezone.$model" :clearable="false" @input="editProjectThrottled"/>
          <div class="validation--error" v-if="$v.project.timezone.$dirty && !$v.project.timezone.required">Timezone is required</div>
        </div>

        <div class="project-form__row">
          <rum-label>Project Address</rum-label>
          <rum-address v-model="$v.project.address.$model" type="text" placeholder="Project Address" @input="editProjectThrottled"/>
          <div class="validation--error" v-if="$v.project.address.$dirty && !$v.project.address.required">Address is required</div>
        </div>

        <div class="project-form__row project-form__slider">
          <rum-label>Branding</rum-label>
          <rum-toggle-button v-model="project.branding" disabled :options="brandingTypes" @input="editProject"></rum-toggle-button>
        </div>
      </header>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import ProjectNewValidation from "@/validations/project-edit";
import throttle from "lodash/throttle";

import { computeTimezoneOptions } from "@/utils/timezones";

import {
  CONNECTIVITY_TYPES,
  BRANDING_TYPES,
  LANGUAGE_ISO_VALUES
} from "@/utils/project";

export default {
  name: "ProjectManageDetails",
  mixins: [ProjectNewValidation],
  data() {
    return {
      organization: null,
      connectivityTypes: CONNECTIVITY_TYPES,
      connectivity: CONNECTIVITY_TYPES[0].value,
      brandingTypes: BRANDING_TYPES,
      branding: BRANDING_TYPES[0].value,
      languages: [],
      languagesIso: LANGUAGE_ISO_VALUES,
      project: {},
      loading: false,
      administeredOrganizations: []
    };
  },
  computed: {
    isManager() {
      return this.$store.getters.isOrgManager;
    },
    projectId() {
      return this.$route.params.id;
    },
    organizationId() {
      return this.organization.id;
    }
  },
  methods: {
    async fetchLanguages() {
      const current = api();
      const { data } = await current.get("rmc/project_languages");
      this.languages = data.languages || [];
    },
    async fetchCurrentProject() {
      const current = api('v3');
      this.loading = true;
      let { data:project } = await current.get(`connect/projects/${this.projectId}`);
      project = project || [];
      project.timezone = computeTimezoneOptions().find(opt => opt.value === project.timezone);
      project.language = this.languagesIso.find(lang => lang.value === project.language);
      this.organization = project.organization;
      this.project = project;
      this.loading = false;
    },
    async fetchOrganization() {
      const current = api('v3');
      this.loading = true;
      const response = await current.get(`users/organizations`);
      this.administeredOrganizations = response.data;
      this.loading = false;
    },
    editProjectThrottled: throttle(function() {
      this.editProject();
    }, 2500),
    async editProject() {
      // HACK: the timezone picker has an issue where it emits a value regardless and
      //  weirdly the project object only has one single key for this short time..
      if (Object.keys(this.project).length === 1) return;

      try {
        const current = api('v3');
        await current.post(`connect/projects/${this.projectId}/edit`, {
          name: this.project.name,
          address: this.project.address,
          internet_connectivity: true,
          timezone: this.project.timezone && this.project.timezone.value,
          language: this.project.language && this.project.language.value,
          branding: "rumie",
          org_id: this.organization.id
        });
        this.$notify({
          type: "success",
          title: "Project Updated Successfully"
        });
      } catch (error) {
        console.log(error);
        const errorMessage = error.response.data.message;
        this.$notify({
          type: "error",
          title: errorMessage || "Failed to update Project, please try again."
        });
      }
    },
  },
  watch: {
    administeredOrganizations: {
      handler() {
        if(this.organization) {
          this.organization = this.administeredOrganizations.filter(org => org.id === this.organization.id)
        }
      }
    },
    project: {
      immediate: true,
      handler() {
        this.$emit("currentProject", this.project)
      }
    }
  },
  async mounted() {
    this.fetchLanguages();
    this.fetchOrganization();
    this.fetchCurrentProject();
  }
};
</script>

<style scoped>
.project-new {
  height: 100%;
  background: var(--white);
  margin: var(--spacing-4);
}

.rumie-spinner--full {
  margin-top: var(--spacing-2) !important;
}

.project__form {
  display: flex;
  flex-direction: column;
  margin: var(--spacing-2);
  margin-left: auto;
  margin-right: auto;
  padding: var(--spacing-3) var(--spacing-6);
  background: var(--white);
  height: 100%;
  max-width: var(--mobile);
  border-radius: var(--border-radius-md);
  margin-bottom: 75px;
}

.project-form__body {
  margin-top: var(--spacing-2);
}

.project-form__row {
  margin-top: var(--spacing-6);
}

.project-form__title {
  width: 100%;
  text-align: center;
}

.project-form__actions {
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  margin-top: var(--spacing-8);
  margin-bottom: var(--spacing-4);
}

.project-form__password {
  margin-top: var(--spacing-6) !important;
  padding: var(--spacing-2);
}

.project-form__info {
  font-size: var(--sm);
  color: var(--tertiary);
}

.project-form__slider {
  margin-bottom: var(--spacing-8);
}

@media screen and (max-width: 500px) {
  .project-form__actions {
    flex-direction: column;
  }

  .project-form__actions button:last-child {
    margin-top: var(--spacing-4);
  }
}
</style>
